import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getFirestore,
  setDoc,
} from "firebase/firestore";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { useAuth } from "./hooks/useAuth";
import "../Styles/personalization.css";

export default function Personalization() {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState(null); 
  const [usernameColor, setUsernameColor] = useState("");
  const [bgColor, setBgColor] = useState("");
  const [presetAvatars, setPresetAvatars] = useState([]);

  useEffect(() => {
    const fetchAvatars = async () => {
      const storage = getStorage();
      const avatarsRef = ref(storage, 'Profile_Pictures');
      try {
        const res = await listAll(avatarsRef);
        const urls = await Promise.all(
          res.items.map((itemRef) => getDownloadURL(itemRef))
        );
        const filteredUrls = urls.filter(url => !url.includes('logo.png'));
        setPresetAvatars(filteredUrls);
        if (userProfile?.avatar) {
          setAvatar(userProfile.avatar);
        } else if (filteredUrls.length > 0) {
          setAvatar(filteredUrls[0]); 
        }
      } catch (error) {
        console.error("Error fetching avatars from storage: ", error);
      }
    };

    fetchAvatars();
  }, [userProfile]);

  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.displayName || "User12");
      setBio(userProfile.bio || "Welcome to Bonfire!");
      setUsernameColor(userProfile.usernameColor || "#c84848");
      setBgColor(userProfile.bgColor || "#ffd9ba");
    }
  }, [userProfile]);

  const handleSave = async () => {
    if (!user) return;

    const db = getFirestore();
    const userRef = doc(db, "users", user.uid);

    try {
      await setDoc(
        userRef,
        {
          ...userProfile, 
          displayName,
          bio,
          avatar,
          usernameColor,
          bgColor,
        },
        { merge: true }
      ); 
      alert("Your customizations have been saved!");
    } catch (error) {
      console.error("Error saving customizations: ", error);
      alert("Could not save changes. Please try again.");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }


  return (
    <div className="personalization-container">
      {/* Back Button */}
      <button
        className="back-btn"
        onClick={() => navigate("/account")}
        aria-label="Go back to Account"
      >
        ← Back to Account
      </button>

      <h1 className="personalization-title">Account Personalization</h1>

      {/* Profile Preview */}
      <div className="top-preview-card" style={{ backgroundColor: bgColor }}>
        <img src={avatar} alt="Avatar" className="top-avatar" />
        <h3 style={{ color: usernameColor }}>{displayName}</h3>
        <p>{bio}</p>
      </div>

      {/* Settings */}
      <div className="personalization-card">
        {/* Avatar Selection */}
        <div className="section">
          <h2>Choose Your Profile Picture</h2>
          <div className="avatar-options">
            {presetAvatars.map((img) => (
              <button
                key={img}
                type="button"
                className={`avatar-choice ${avatar === img ? "selected-avatar" : ""}`}
                onClick={() => setAvatar(img)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") setAvatar(img);
                }}
                aria-label="Select avatar"
                tabIndex={0}
              >
                <img
                  src={img}
                  alt="Avatar option"
                  draggable={false}
                  style={{ pointerEvents: "none" }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Display Name */}
        <div className="section">
          <h2>Display Name</h2>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={{ color: usernameColor }}
          />
        </div>

        {/* Username Color */}
        <div className="section hidden">
          <h2>Username Color</h2>
          <input
            type="color"
            value={usernameColor}
            onChange={(e) => setUsernameColor(e.target.value)}
          />
        </div>

        {/* Background Color */}
        <div className="section hidden">
          <h2>Profile Background Color</h2>
          <input
            type="color"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
          />
        </div>

        {/* Bio */}
        <div className="section">
          <h2>Bio</h2>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Write something about yourself..."
          />
        </div>

        {/* Save Button */}
        <button className="save-btn" onClick={handleSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
}
